import type { AdsBatchTemplateizeResult } from "@/lib/adsBatchTemplateizeCore";
import type { useAdsBatchTemplateize } from "@/components/ads-assistant/useAdsBatchTemplateize";
import { excerptForBatch } from "@/components/ads-assistant/adsBatchTemplateizeExcerpt";

export function AdsBatchTemplateizeReviewStep(props: {
  vm: ReturnType<typeof useAdsBatchTemplateize>;
}) {
  const { vm } = props;
  const proposalRows = Array.from(vm.results.values()).filter(
    (r): r is Extract<AdsBatchTemplateizeResult, { status: "proposed" }> => r.status === "proposed",
  );
  const otherRows = Array.from(vm.results.values()).filter((r) => r.status !== "proposed");
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300">
        <span>
          <strong className="tabular-nums">{vm.summary.proposed_with_template}</strong> matched ·{" "}
          <strong className="tabular-nums">{vm.summary.proposed_no_fit}</strong> no fit ·{" "}
          <strong className="tabular-nums">{vm.summary.errors}</strong> errors ·{" "}
          <strong className="tabular-nums">{vm.summary.accepted}</strong> accepted
        </span>
        <button
          type="button"
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={vm.acceptAllWithTemplate}
        >
          Accept all matched
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={vm.acceptNone}
        >
          Accept none
        </button>
      </div>
      <ul className="mt-3 max-h-[50vh] divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {proposalRows.map((r) => {
          const post = vm.posts.find((p) => p.id === r.post_id);
          return (
            <li key={r.post_id} className="flex items-start gap-2 px-3 py-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={r.accepted}
                disabled={!r.template_id}
                onChange={() => vm.toggleAccept(r.post_id)}
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-violet-900 dark:text-violet-200">
                  {r.template_name ?? (r.template_id ? "(template)" : "No template fits")}
                </p>
                {r.notes ? (
                  <p className="mt-0.5 text-[11px] italic text-gray-600 dark:text-gray-400">{r.notes}</p>
                ) : null}
                <p className="mt-1 break-words text-[11px] text-gray-700 dark:text-gray-300">
                  {excerptForBatch(post?.text_content ?? "")}
                </p>
              </div>
            </li>
          );
        })}
        {otherRows.map((r) => (
          <li key={r.post_id} className="px-3 py-2 text-[11px] text-gray-600 dark:text-gray-400">
            {r.status === "error"
              ? `Error: ${r.error}`
              : r.status === "skipped"
                ? `Skipped: ${r.reason}`
                : r.status}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={vm.closeDialog}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!vm.summary.accepted}
          className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          onClick={() => void vm.applyAccepted()}
        >
          Apply {vm.summary.accepted} accepted
        </button>
      </div>
    </>
  );
}
